import { compareAppVersions, isAppVersionSupported } from './appVersion';

/** Manifest published next to the APK on each live release. */
export type AndroidVersionManifest = {
  versionName: string;
  versionCode: number;
  apkStoragePath?: string;
  /** Soft-block floor: versions below this must update (optional). */
  forceBelow?: string | null;
  publishedAt?: string;
  notes?: string;
};

export type AndroidInstalledVersion = {
  versionName: string;
  versionCode: number;
};

export type AndroidUpdateOffer = {
  available: boolean;
  force: boolean;
  remote: AndroidVersionManifest;
  installed: AndroidInstalledVersion;
};

export function parseAndroidVersionManifest(raw: unknown): AndroidVersionManifest | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const versionName = String(row.versionName ?? '').trim();
  const versionCode = Number(row.versionCode);
  if (!versionName || !Number.isFinite(versionCode) || versionCode < 1) return null;
  return {
    versionName,
    versionCode: Math.floor(versionCode),
    apkStoragePath: row.apkStoragePath != null ? String(row.apkStoragePath) : undefined,
    forceBelow: row.forceBelow != null ? String(row.forceBelow) : null,
    publishedAt: row.publishedAt != null ? String(row.publishedAt) : undefined,
    notes: row.notes != null ? String(row.notes) : undefined,
  };
}

/**
 * Decide whether to offer an update (Play Store–like soft / force).
 * Prefer versionCode; fall back to semver name compare.
 */
export function evaluateAndroidUpdateOffer(
  installed: AndroidInstalledVersion,
  remote: AndroidVersionManifest,
  options: { minAppVersion?: string } = {},
): AndroidUpdateOffer {
  const codeNewer = remote.versionCode > installed.versionCode;
  const nameNewer = compareAppVersions(remote.versionName, installed.versionName) > 0;
  const available = codeNewer || nameNewer;

  const forceByManifest = Boolean(
    remote.forceBelow
    && !isAppVersionSupported(installed.versionName, remote.forceBelow),
  );
  const forceByRemoteConfig = Boolean(
    options.minAppVersion
    && !isAppVersionSupported(installed.versionName, options.minAppVersion),
  );

  return {
    available: available || forceByManifest || forceByRemoteConfig,
    force: forceByManifest || forceByRemoteConfig,
    remote,
    installed,
  };
}
